<template>
    <main-dialog
        ref="dialog"
        heading="parkingLot.allocatePromotion.heading"
        btn-confirm-text="actions.allocate"
        :has-activator="hasActivator"
        :confirm-btn-disabled="allocateInProgress"
        width="800"
        @confirm="allocatePromotionsDialogConfirm"
        @close="closeDialog"
    >
        <template v-if="hasActivator" v-slot:actions="{ activator }">
            <icon v-bind="activator" icon-name="duplicate" small @click.stop />
        </template>
        <div class="allocate-promotion">
            <p class="allocate-promotion__description">
                {{ toSentenceCase($t('parkingLot.allocatePromotion.description')) }}
            </p>
            <vuex-select
                class="py-2"
                :placeholder="$t('entities.subCampaigns', 2)"
                :options="availableSubCampaigns"
                item-text="name"
                item-value="_id"
                :getter="() => selectedSubCampaigns"
                :setter="value => (selectedSubCampaigns = value)"
                multiple
                chips
                deletable-chips
                clearable
                preserve-input-on-select
                density="compact"
            />
        </div>

        <div v-if="requestInProgress" class="in-progress--message">
            <label class="in-progress__message">{{
                toSentenceCase($t('parkingLot.allocatePromotion.inProgressMessage'))
            }}</label>
            <spinner-dynamic />
        </div>
    </main-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
    props: {
        hasActivator: {
            type: Boolean,
            default: true,
        },
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        promotionIds: {
            type: Array,
            required: true,
        },
        successCallback: {
            type: Function,
            required: false,
        },
    },
    data() {
        return {
            allocateInProgress: false,
            availableSubCampaigns: [],
            selectedSubCampaigns: [],
        };
    },
    computed: {
        ...mapState('promotions', ['loading']),
        ...mapGetters('weeks', ['getWeekByDate']),
        requestInProgress() {
            return this.loading;
        },
    },
    methods: {
        ...mapActions('promotions', ['movePromotionsToScenario']),
        ...mapActions('notifications', ['addNotification']),
        async openDialog() {
            this.availableSubCampaigns = await this.fetchSubCampaignsForAllocation({
                promotionIds: this.promotionIds,
                startDate: this.startDate,
                endDate: this.endDate,
            });
            this.$refs.dialog.open();
        },

        closeDialog() {
            this.availableSubCampaigns = [];
            this.selectedSubCampaigns = [];
            this.$refs.dialog.close();
        },

        async fetchSubCampaignsForAllocation({ promotionIds, startDate, endDate }) {
            const { data: subCampaigns } = await this.$http.get(
                '/api/sub-campaigns/getSuitableSubCampaignsForPromotions',
                {
                    params: {
                        promotionIds,
                        startDate,
                        endDate,
                    },
                }
            );
            return subCampaigns;
        },

        async copyPromotions() {
            const response = await this.$http.post(
                '/api/sub-campaigns/allocateMultiplePromotions',
                {
                    promotionIds: this.promotionIds,
                    subCampaignIds: this.selectedSubCampaigns,
                }
            );
            if (response.status === 200) {
                this.closeDialog();
                this.addNotification({ message: this.$tkey('notifications.allocationSuccess') });
            }
        },

        async allocatePromotionsDialogConfirm() {
            this.allocateInProgress = true;
            await this.copyPromotions();
            this.allocateInProgress = false;
            if (this.successCallback) this.successCallback(this.promotionIds);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.allocate-promotion {
    font-size: 1.2rem;

    &__description {
        padding: 1rem 0;
    }
}

.in-progress__message {
    font-size: 1.3rem;
    color: $promo-text-colour;
    margin: 1rem;

    .spinner {
        width: 1.4rem;
        height: 1.4rem;
    }
}

:deep(.v-text-field__slot) {
    label {
        padding-left: 1rem;
    }
}
</style>
